import { css } from '@emotion/react';
import type { ReactNode } from 'react';
import { LogoComponent } from '~/_shared/components/logo/logo.component';
import { MAPTIVE_WEBSITE } from '~/_shared/constants/maptive';
import type { Theme } from '~/_shared/themes/theme.model';
import { scaleMmToFitPage } from '~/exportReport/printPage/exportReportPrintPage.helpers';

const HEADER_HEIGHT_PX = 75;
const HEADER_TOP_GAP_PX = 16;
const HEADER_BOTTOM_GAP_PX = 8;
const HEADER_SPACE_PX = HEADER_TOP_GAP_PX + HEADER_HEIGHT_PX + HEADER_BOTTOM_GAP_PX;
const FOOTER_HEIGHT_PX = 50;
const FIRST_PAGE_TOP_MARGIN = '8mm';

// Chrome doesn't paint @page margins, so the header/footer are fixed in the page
// and the content's cloned top/bottom padding reserves their space on every page.
const fixedBarStyle = css({
  boxSizing: 'border-box',
  left: '50%',
  position: 'fixed',
  transform: 'translateX(-50%)',
  width: scaleMmToFitPage(190),
  zIndex: 1,
});

const linkStyle = css({
  color: 'inherit',
  textDecoration: 'none',
});

const headerStyle = (theme: Theme) => css({
  alignItems: 'center',
  backgroundColor: theme.reportColors.sectionBackground,
  borderRadius: 4,
  boxShadow: `0px 1px 2px 0px ${theme.reportColors.boxShadow}`,
  color: theme.reportColors.textPrimary,
  display: 'flex',
  gap: 16,
  height: HEADER_HEIGHT_PX,
  justifyContent: 'space-between',
  padding: '0 16px',
  top: HEADER_TOP_GAP_PX,
});

const reportNameStyle = css({
  fontSize: 20,
  fontWeight: 600,
  textTransform: 'uppercase',
});

const brandStyle = css({
  alignItems: 'stretch',
  display: 'flex',
  flexDirection: 'column',
  width: 120,
});

const brandDividerStyle = (theme: Theme) => css({
  borderTop: `1px solid ${theme.reportColors.borderPrimary}`,
  height: 0,
  margin: '4px 0',
});

const brandUrlStyle = (theme: Theme) => css({
  color: theme.reportColors.textSecondary,
  fontSize: 11,
  textAlign: 'center',
});

const footerStyle = (theme: Theme) => css({
  alignItems: 'center',
  bottom: 0,
  color: theme.reportColors.textSecondary,
  display: 'flex',
  fontSize: 11,
  height: FOOTER_HEIGHT_PX,
  justifyContent: 'center',
  padding: '0 16px',
});

// Page 1 has its own header: cover the repeating one and pull the content up.
const firstPageHeaderCoverStyle = (theme: Theme) => css({
  backgroundColor: theme.reportColors.backgroundPrimary,
  height: HEADER_SPACE_PX,
  left: 0,
  position: 'absolute',
  right: 0,
  top: 0,
  zIndex: 2,
});

const contentStyle = css({
  boxDecorationBreak: 'clone',
  marginTop: `calc(${FIRST_PAGE_TOP_MARGIN} - ${HEADER_SPACE_PX}px)`,
  padding: `${HEADER_SPACE_PX}px 0 ${FOOTER_HEIGHT_PX}px`,
  position: 'relative',
  zIndex: 3,
});

type ExportReportPrintPageHeaderProps = Readonly<{
  reportName: string;
}>;

const ExportReportPrintPageHeaderComponent = ({ reportName }: ExportReportPrintPageHeaderProps) => (
  <div css={[fixedBarStyle, headerStyle]}>
    <div css={reportNameStyle}>{reportName}</div>
    <div css={brandStyle}>
      <LogoComponent width="120px" />
      <div css={brandDividerStyle} />
      <div css={brandUrlStyle}>
        <a css={linkStyle} href={MAPTIVE_WEBSITE}>www.maptive.com</a>
      </div>
    </div>
  </div>
);

const ExportReportPrintPageFooterComponent = () => {
  const createdOn = new Date().toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div css={[fixedBarStyle, footerStyle]}>
      <span>
        {'Report generated by Maptive · '}
        <a css={linkStyle} href={MAPTIVE_WEBSITE}>maptive.com</a>
        {` · ${createdOn}`}
      </span>
    </div>
  );
};

type ExportReportPrintPageLayoutProps = Readonly<{
  children: ReactNode;
  reportName: string;
}>;

// Must be rendered inside a positioned element: the page-1 cover is positioned against it.
export const ExportReportPrintPageLayoutComponent = ({ reportName, children }: ExportReportPrintPageLayoutProps) => (
  <>
    <div css={firstPageHeaderCoverStyle} />
    <ExportReportPrintPageHeaderComponent reportName={reportName} />
    <div css={contentStyle}>{children}</div>
    <ExportReportPrintPageFooterComponent />
  </>
);
